import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Score } from '@sorare/core/src/components/scoring/Score';

import { So5AppearanceScore } from 'components/appearance/So5AppearanceScore';
import { Captain } from 'components/so5/Captain';

import {
  BriefPlayerScore,
  Props as BriefPlayerScoreProps,
} from '../BriefPlayerScore';
import { BriefSo5AppearanceScore_so5Appearance } from './__generated__/index.graphql';

const CaptainWrapper = styled.div`
  position: absolute;
  top: -6px;
  left: -6px;
`;

type Props = { so5Appearance: BriefSo5AppearanceScore_so5Appearance } & Pick<
  BriefPlayerScoreProps,
  'disableLinks'
>;

export const BriefSo5AppearanceScore = ({ so5Appearance, ...rest }: Props) => {
  const {
    anyCard,
    anyPlayer,
    eligiblePlayerGameScores,
    pictureUrl,
    scoreStatus,
    scoreWithoutBonus,
    bonus,
    captain,
  } = so5Appearance;

  return (
    <BriefPlayerScore
      {...rest}
      anyCard={anyCard}
      cardBadge={
        captain && (
          <CaptainWrapper>
            <Captain active size={2} />
          </CaptainWrapper>
        )
      }
      anyPlayer={anyPlayer}
      playerGameScores={eligiblePlayerGameScores}
      pictureUrl={pictureUrl || ''}
      bonus={bonus}
      scoreWithoutBonus={
        eligiblePlayerGameScores.length > 1 && (
          <Score
            shape="HEXAGON"
            scoreType="PLAYER_SCORE"
            score={scoreWithoutBonus}
            scoreStatus={scoreStatus}
          />
        )
      }
    >
      <So5AppearanceScore so5Appearance={so5Appearance} />
    </BriefPlayerScore>
  );
};

BriefSo5AppearanceScore.fragments = {
  so5Appearance: gql`
    fragment BriefSo5AppearanceScore_so5Appearance on So5Appearance {
      id
      captain
      eligiblePlayerGameScores {
        id
        ...BriefPlayerScore_playerGameScoreInterface
      }
      score
      scoreWithoutBonus: score(withBonus: false)
      scoreStatus
      pictureUrl
      bonus
      anyPlayer {
        slug
        ...BriefPlayerScore_anyPlayerInterface
      }
      anyCard {
        slug
        ...BriefPlayerScore_anyCardInterface
      }
      ...So5AppearanceScore_so5Appearance
    }
    ${BriefPlayerScore.fragments.anyCardInterface}
    ${BriefPlayerScore.fragments.anyPlayerInterface}
    ${BriefPlayerScore.fragments.playerGameScoreInterface}
    ${So5AppearanceScore.fragments.so5Appearance}
  ` as TypedDocumentNode<BriefSo5AppearanceScore_so5Appearance>,
};
